import { ChangeEvent, useEffect, useState } from "react";

import { Autocomplete, SelectEvent } from "@king-ict/eupisi-ui/components";
import { CodeBook } from "@king-ict/eupisi-ui/models/Common/CodeBook";
import { codeBookToSelectData } from "@king-ict/eupisi-ui/utils";

import { useGetCountiesQuery } from "../../queries/codeBookQuery";
import { Placement } from "react-bootstrap/esm/types";

export interface CountySelectProps {
  value?: number;
  name?: string;
  disabled?: boolean;
  readOnly?: boolean;
  label?: string;
  errorText?: string;
  /**
   * Filter to limit county selection. Use for authorization.
   */
  countyIds?: number[];
  excludeCountyIds?: number[];
  allowEmpty?: boolean;
  dropDownDirection?: Placement;
  canFlip?: boolean;
  onChange: (event: ChangeEvent<SelectEvent>) => void;
}

/**
 * This is a single select component based on an AutoComplete
 * (combobox - text + dropdown) for all Counties (no internal authorization)
 * and may be used where an CountyId is optional. To change the selection,
 * the current selection must be cleared by clicking the 'X', afterwhich
 * a new selection may be made. Between selections, the selected value
 * will be undefined.
 * @param param0
 * @returns
 */
export const CountySelect = ({
  value,
  label,
  disabled = false,
  errorText,
  onChange,
  countyIds,
  allowEmpty = false,
  excludeCountyIds,
  dropDownDirection,
  canFlip,
}: CountySelectProps) => {
  const [filteredCounties, setFilteredCounties] = useState<CodeBook[]>();
  const { data: counties } = useGetCountiesQuery();

  useEffect(() => {
    (async () => {
      setFilteredCounties(
        counties
          ?.filter(
            (x) => !countyIds || countyIds.find((v) => x.id === v) !== undefined
          )
          .filter(
            (x) =>
              !excludeCountyIds ||
              excludeCountyIds.find((v) => x.id === v) === undefined
          )
      );
    })();
  }, [countyIds, excludeCountyIds, counties]);

  useEffect(() => {
    if (filteredCounties && filteredCounties.length > 0) {
      if (!allowEmpty) {
        if (
          filteredCounties.length === 1 || // We only have one so use it
          value === undefined || // We must set something
          !filteredCounties.some((v) => v.id === value)
        ) {
          // The desired value is not in list
          value = filteredCounties[0].id;
        }
      }
    }
  }, [filteredCounties]);

  return (
    <Autocomplete
      label={label}
      name={"County"}
      value={value}
      disabled={disabled}
      selectData={codeBookToSelectData(filteredCounties)}
      onChange={onChange}
      errorText={errorText}
      dropdownDirection={dropDownDirection}
      canFlip={canFlip}
    />
  );
};
